import type { TrainingAnswer, TrainingPlayerQuestion } from '@hcc/types';
import { RadioAnswer } from './answers/RadioAnswer';
import { CheckboxAnswer } from './answers/CheckboxAnswer';
import { TrueFalseAnswer } from './answers/TrueFalseAnswer';
import { ShortTextAnswer } from './answers/ShortTextAnswer';
import { CategorizeAnswer } from './answers/CategorizeAnswer';
import { OrderAnswer } from './answers/OrderAnswer';
import { HotspotAnswer } from './answers/HotspotAnswer';
import { MatchAnswer } from './answers/MatchAnswer';

/**
 * One question page: prompt + optional illustration + the answer widget
 * for its type. Purely presentational over the answer-free player shapes;
 * the widgets never see correctness.
 */
export function QuestionSlide({
  question,
  answer,
  onChange,
  disabled,
}: {
  question: TrainingPlayerQuestion;
  answer: TrainingAnswer | undefined;
  onChange: (next: TrainingAnswer) => void;
  disabled?: boolean;
}) {
  // Name the answer group after the prompt so a screen reader announces the
  // question on entering the radiogroup / group, not a bare "radio group".
  const promptId = `q-prompt-${question.id}`;
  return (
    <div className="space-y-4">
      <h2 id={promptId} className="text-lg font-semibold text-fg">
        {question.prompt}
        {question.optional ? (
          <span className="ml-2 text-sm font-normal text-fg-dim">(optional)</span>
        ) : null}
      </h2>
      {question.type !== 'hotspot' && question.image_key ? (
        <img
          src={question.image_key}
          alt=""
          className="max-h-64 max-w-full rounded-token-sm border border-line/10"
        />
      ) : null}
      <AnswerWidget
        question={question}
        answer={answer}
        onChange={onChange}
        disabled={disabled}
        labelledBy={promptId}
      />
    </div>
  );
}

function AnswerWidget({
  question,
  answer,
  onChange,
  disabled,
  labelledBy,
}: {
  question: TrainingPlayerQuestion;
  answer: TrainingAnswer | undefined;
  onChange: (next: TrainingAnswer) => void;
  disabled?: boolean;
  labelledBy: string;
}) {
  switch (question.type) {
    case 'single_choice':
      return (
        <RadioAnswer
          options={question.options}
          value={typeof answer === 'string' ? answer : undefined}
          onChange={onChange}
          disabled={disabled}
          ariaLabelledBy={labelledBy}
        />
      );
    case 'multi_select':
      return (
        <CheckboxAnswer
          options={question.options}
          value={Array.isArray(answer) ? answer : undefined}
          onChange={onChange}
          disabled={disabled}
          ariaLabelledBy={labelledBy}
        />
      );
    case 'true_false':
      return (
        <TrueFalseAnswer
          value={typeof answer === 'boolean' ? answer : undefined}
          onChange={onChange}
          disabled={disabled}
          ariaLabelledBy={labelledBy}
        />
      );
    case 'short_text':
      return (
        <ShortTextAnswer
          value={typeof answer === 'string' ? answer : undefined}
          onChange={onChange}
          disabled={disabled}
          ariaLabelledBy={labelledBy}
          multiline={question.multiline}
          placeholder={question.placeholder}
        />
      );
    case 'categorize':
      return (
        <CategorizeAnswer
          categories={question.categories}
          items={question.items}
          value={
            typeof answer === 'object' && answer !== null && !Array.isArray(answer)
              ? (answer as Record<string, string>)
              : undefined
          }
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'ordering':
      return (
        <OrderAnswer
          items={question.items}
          value={Array.isArray(answer) ? answer : undefined}
          onChange={onChange}
          disabled={disabled}
          ariaLabelledBy={labelledBy}
        />
      );
    case 'hotspot':
      return (
        <HotspotAnswer
          imageKey={question.image_key}
          value={
            typeof answer === 'object' && answer !== null && !Array.isArray(answer)
              ? (answer as { x: number; y: number })
              : undefined
          }
          onChange={onChange}
          disabled={disabled}
          ariaLabelledBy={labelledBy}
        />
      );
    case 'matching':
      return (
        <MatchAnswer
          targets={question.targets}
          labels={question.labels}
          value={
            typeof answer === 'object' && answer !== null && !Array.isArray(answer)
              ? (answer as Record<string, string>)
              : undefined
          }
          onChange={onChange}
          disabled={disabled}
          ariaLabelledBy={labelledBy}
        />
      );
  }
}
